// v2.1 (D): the auth calls beyond src/api.ts (redeem an invite, request access). Mock mode (VITE_MOCK=1) answers them
// in memory; `?access=pending` there starts the session as a pending Clerk sign-up, and `?access=suspended` as a
// suspended account, so the gate can be screenshotted. v4 LG2: `&geo=1` makes the pending account one from a country
// where sign-ups are closed, and `?terms=1` asks for the Terms checkbox first (`?adult=1` the 18+ box; `?locked=1` the
// "account paused" page).
import { ApiError, call, normUser } from "../api";
import type { Me, User } from "../types";
import { MOCK } from "./state";

const q = typeof location === "undefined" ? new URLSearchParams() : new URLSearchParams(location.search);
let mockPending = MOCK && q.get("access") === "pending";
const mockSuspended = MOCK && q.get("access") === "suspended";
let mockRequestedAt: string | null = null;
let mockGeo = MOCK && q.get("geo") === "1";
let mockTerms = MOCK && q.get("terms") === "1";
// Wave 1 safety: `?adult=1` asks for the 18+ box (the Terms screen); `?locked=1` starts the account locked
let mockAdult = MOCK && q.get("adult") === "1";
const mockLocked = MOCK && q.get("locked") === "1";
const MOCK_TERMS_VERSION = "mock";

/** Mock only: the signed-in user as the gate should see them. */
export function withMockAccess(me: Me): Me {
  if (!MOCK) return me;
  return {
    ...me,
    user: {
      ...me.user,
      access: mockSuspended ? "suspended" : mockPending ? "pending" : "active",
      accessRequestedAt: mockRequestedAt,
      geoBlocked: mockPending && mockGeo,
      termsRequired: mockTerms,
      currentTermsVersion: MOCK_TERMS_VERSION,
      adultRequired: mockAdult,
      safetyLocked: mockLocked,
    },
  };
}

export const authApi = {
  /** POST /api/auth/redeem-invite { code } -> { user, balance } */
  async redeemInvite(code: string): Promise<{ user: User; balance: number }> {
    if (MOCK) {
      await new Promise((r) => setTimeout(r, 400));
      if (code.trim().length < 4) throw new ApiError("That invite code doesn't exist.", 404);
      mockPending = false;
      mockGeo = false;
      return { user: { id: "u_1", email: "keaton@example.com", name: "Keaton", role: "admin", access: "active" }, balance: 750 };
    }
    const o = await call<{ user: unknown; balance: number }>("POST", "auth/redeem-invite", { code: code.trim() });
    return { user: normUser(o.user), balance: Number(o.balance) || 0 };
  },
  /** POST /api/auth/link-password { email, password } -> { user, balance }: signed in with Clerk, connect the old
   *  password account with that email (its projects and credits) to this sign-in. */
  async linkPassword(email: string, password: string): Promise<{ user: User; balance: number }> {
    if (MOCK) {
      await new Promise((r) => setTimeout(r, 400));
      if (password.length < 8) throw new ApiError("That email and password don't match.", 401);
      mockPending = false;
      return { user: { id: "u_1", email, name: "Keaton", role: "admin", access: "active" }, balance: 2208 };
    }
    const o = await call<{ user: unknown; balance: number }>("POST", "auth/link-password", { email: email.trim(), password });
    return { user: normUser(o.user), balance: Number(o.balance) || 0 };
  },
  /** v4 LG2: POST /api/me/accept-terms { version, adult } -> { user }. `version` is the one /api/me asked for; Wave 1
   *  safety: `adult: true` is the "I'm 18 or older" box (the api answers 400 ADULT_REQUIRED without it until it's set). */
  async acceptTerms(version: string, adult: true): Promise<User> {
    if (MOCK) {
      await new Promise((r) => setTimeout(r, 300));
      if (adult !== true) throw new ApiError("Please confirm you're 18 or older.", 400, "ADULT_REQUIRED");
      mockTerms = false;
      mockAdult = false;
      return { id: "u_1", email: "keaton@example.com", name: "Keaton", role: "admin", termsRequired: false, termsVersion: version, adultRequired: false };
    }
    const o = await call<{ user: unknown }>("POST", "me/accept-terms", { version, adult });
    return normUser(o.user);
  },
  /** v4 LG2: POST /api/waitlist { email } -> { ok }. Works signed out (the sign-up screen) and signed in. */
  async joinWaitlist(email: string): Promise<void> {
    if (MOCK) {
      await new Promise((r) => setTimeout(r, 300));
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) throw new ApiError("That doesn't look like an email address.", 400);
      return;
    }
    await call("POST", "waitlist", { email: email.trim() });
  },
  /** POST /api/auth/request-access { note? } -> { user } */
  async requestAccess(note?: string): Promise<User> {
    if (MOCK) {
      await new Promise((r) => setTimeout(r, 300));
      mockRequestedAt ??= new Date().toISOString();
      return { id: "u_1", email: "keaton@example.com", name: "Keaton", role: "user", access: mockSuspended ? "suspended" : "pending", accessRequestedAt: mockRequestedAt };
    }
    const o = await call<{ user: unknown }>("POST", "auth/request-access", { note: note?.trim() || null });
    return normUser(o.user);
  },
};
